/**
 * «Avisarme en este móvil»: el botón que apunta ESTE navegador a los avisos.
 *
 * ── Por qué dice «este» y no «mi» ───────────────────────────────────────────
 *
 * Porque la suscripción es de un navegador, no de una persona. Jorge puede
 * querer el aviso en el móvil y no en el portátil, y llamarlo «mi móvil»
 * haría creer que se configura una vez y vale para todo. Se pulsa en cada
 * aparato donde se quiera el aviso.
 *
 * ── Por qué manda uno de prueba al terminar ─────────────────────────────────
 *
 * Porque un botón que dice «listo» y no avisa es el peor de los dos mundos.
 * Entre el permiso del navegador, la clave y el trabajador de fondo hay tres
 * cosas que pueden fallar en silencio, y la única forma de saber que las tres
 * están bien es que llegue uno.
 */
import { useCallback, useEffect, useState } from "react";

/** Base64 de la URL a los bytes que pide el navegador. */
function aBytes(base64: string): ArrayBuffer {
  const relleno = "=".repeat((4 - (base64.length % 4)) % 4);
  const limpio = (base64 + relleno).replace(/-/g, "+").replace(/_/g, "/");
  const crudo = window.atob(limpio);
  const bytes = new Uint8Array(crudo.length);
  for (let i = 0; i < crudo.length; i++) bytes[i] = crudo.charCodeAt(i);
  return bytes.buffer;
}

type Estado = "mirando" | "no-se-puede" | "sin-apuntar" | "apuntado" | "trabajando";

export function AvisarmeEnEsteMovil() {
  const [estado, setEstado] = useState<Estado>("mirando");
  const [porQueNo, setPorQueNo] = useState<string | null>(null);
  const [dicho, setDicho] = useState<string | null>(null);

  const mirar = useCallback(async () => {
    /*
     * Tres cosas que pueden faltar, y cada una con su motivo: «no se puede»
     * sin decir cuál de las tres es una pantalla en la que no se puede hacer
     * nada.
     */
    if (!("serviceWorker" in navigator)) {
      setPorQueNo("Este navegador no sabe de trabajadores de fondo.");
      return setEstado("no-se-puede");
    }
    if (!("PushManager" in window)) {
      setPorQueNo("Este navegador no admite avisos push.");
      return setEstado("no-se-puede");
    }
    if (!window.isSecureContext) {
      setPorQueNo("Los avisos piden https. En http el navegador no los deja ni pedir.");
      return setEstado("no-se-puede");
    }
    if (Notification.permission === "denied") {
      setPorQueNo(
        "Los avisos están bloqueados para esta web en los ajustes del navegador. " +
          "Hay que permitirlos ahí: desde aquí ya no se puede volver a preguntar.",
      );
      return setEstado("no-se-puede");
    }

    const registro = await navigator.serviceWorker.getRegistration();
    const suya = await registro?.pushManager.getSubscription();
    setEstado(suya ? "apuntado" : "sin-apuntar");
  }, []);

  useEffect(() => {
    void mirar();
  }, [mirar]);

  async function apuntarme() {
    setEstado("trabajando");
    setDicho(null);
    try {
      const permiso = await Notification.requestPermission();
      if (permiso !== "granted") {
        setPorQueNo("No has dado permiso. Sin él el navegador no deja mandar nada.");
        return setEstado("no-se-puede");
      }

      const registro = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      const r = await fetch("/api/avisos/clave", { credentials: "same-origin" });
      const { publica } = (await r.json()) as { publica: string };

      const suscripcion = await registro.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: aBytes(publica),
      });

      const s = suscripcion.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
      await fetch("/api/avisos/apuntarme", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...s, comoSeLlama: navigator.userAgent.slice(0, 60) }),
      });

      /* Y uno de prueba, que es lo único que demuestra que las tres cosas van. */
      const p = await fetch("/api/avisos/probar", { method: "POST", credentials: "same-origin" });
      const como = (await p.json()) as { enviados?: number; error?: string };
      setDicho(
        como.enviados
          ? "Apuntado. Te acabo de mandar uno de prueba: si no llega en unos segundos, algo falla y hay que mirarlo."
          : `Apuntado, pero el de prueba no salió: ${como.error ?? "sin motivo"}`,
      );
      setEstado("apuntado");
    } catch (e) {
      setPorQueNo((e as Error).message);
      setEstado("no-se-puede");
    }
  }

  async function quitarme() {
    setEstado("trabajando");
    try {
      const registro = await navigator.serviceWorker.getRegistration();
      const suya = await registro?.pushManager.getSubscription();
      if (suya) {
        await fetch("/api/avisos/quitarme", {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ endpoint: suya.endpoint }),
        });
        await suya.unsubscribe();
      }
      setDicho("Quitado. Este aparato deja de recibir avisos.");
      setEstado("sin-apuntar");
    } catch (e) {
      setPorQueNo((e as Error).message);
      setEstado("no-se-puede");
    }
  }

  return (
    <div className="avisos-movil">
      <h3>Avisos en este aparato</h3>
      <p className="avisos-que-son">
        Dos cosas y ninguna más: <strong>las tareas pendientes</strong>, una vez al día a las 9:00 y sólo si hay
        alguna; y <strong>las alertas</strong> —web caída, formulario roto— al momento. Tocar el aviso abre la
        pestaña de Tareas pendientes.
      </p>

      {estado === "mirando" && <p className="avisos-estado">Mirando si este navegador puede…</p>}

      {estado === "no-se-puede" && (
        <p className="avisos-estado avisos-mal">No se puede en este aparato: {porQueNo}</p>
      )}

      {estado === "sin-apuntar" && (
        <button className="avisos-boton" onClick={() => void apuntarme()}>
          Avisarme en este móvil
        </button>
      )}

      {estado === "apuntado" && (
        <>
          <p className="avisos-estado avisos-bien">Este aparato está apuntado.</p>
          <button className="avisos-boton avisos-quitar" onClick={() => void quitarme()}>
            Dejar de avisarme aquí
          </button>
        </>
      )}

      {estado === "trabajando" && <p className="avisos-estado">Un momento…</p>}
      {dicho && <p className="avisos-estado">{dicho}</p>}
    </div>
  );
}
